import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { AlertTriangle, CheckSquare, ExternalLink, FileDown, FileText, LayoutGrid, Package, Printer, Scissors, Search, Square, Tag, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button, Card, Field, Input, Select, Spinner, StatusBadge } from "../../components/ui-kit";
import { formatItalianDate } from "../../lib/dateUtils";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { elementToPdf, pdfFileName, preparePdfWindow } from "../../lib/pdfExport";
import { QrLabel62x100Sheet, printQrLabels62x100, qrLabels62x100Pdf } from "../../components/QrLabel62x100";

// QR materie prime (routers_raw_material_qr.py): un QR FISSO per ogni materia
// prima della sede, da applicare su contenitore, scaffale o cella. Si stampa
// una volta: a ogni nuovo lotto registrato (ricevimento merci o lotto singolo)
// la scheda pubblica /mp/<token> si aggiorna da sola con lotto, fornitore,
// scadenza e DDT di appartenenza. Il cartellino stampato non riporta il lotto
// proprio perché il lotto cambia: lo mostra solo la scheda alla scansione.
const qrUrl = (row) => `${window.location.origin}/mp/${row.token}`;

const STATUS = {
  valido: { sem: "verde", label: "Lotto valido" },
  in_scadenza: { sem: "giallo", label: "In scadenza" },
  scaduto: { sem: "rosso", label: "Scaduto" },
  non_indicata: { sem: "giallo", label: "Scadenza mancante" },
  nessun_lotto: { sem: "grigio", label: "Nessun lotto" },
};

const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "in_use", label: "Con lotto in uso" },
  { key: "alert", label: "Scadute / in scadenza" },
  { key: "no_doc", label: "DDT da completare" },
  { key: "nessun_lotto", label: "Senza lotto" },
];

const FORMATS = {
  grid: { label: "Tavola A4 — 12 QR", hint: "Quadretto riepilogativo per dispensa o cella.", icon: LayoutGrid },
  cards: { label: "Cartellini A4 — 6 da ritagliare", hint: "Un cartellino grande per contenitore o scaffale.", icon: Scissors },
  label62: { label: "Etichetta singola 62×100", hint: "Un'etichetta adesiva 62×100 mm per ogni materia prima selezionata.", icon: Tag },
};

const missingDoc = (r) => r.lots_in_use > 0 && (!r.document || r.document.pending || !r.document.number);

export default function QrMateriePrime() {
  const { ctx } = useOutletContext();
  const sites = ctx.sites || [];
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [rows, setRows] = useState(null);
  const [expiringDays, setExpiringDays] = useState(7);
  const [selected, setSelected] = useState([]);
  const [zone, setZone] = useState("");
  const [format, setFormat] = useState("grid");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!siteId) { setRows([]); return; }
    setRows(null);
    api.get("/app/raw-material-qr", { params: { site_id: siteId } })
      .then(({ data }) => {
        const list = data.materials || [];
        setRows(list);
        setExpiringDays(data.expiring_days || 7);
        setSelected(list.filter((r) => r.lots_in_use > 0).map((r) => r.ingredient_id));
      })
      .catch(() => { setRows([]); toast.error("Impossibile caricare le materie prime"); });
  }, [siteId]);

  const stats = useMemo(() => {
    const list = rows || [];
    return {
      total: list.length,
      inUse: list.filter((r) => r.lots_in_use > 0).length,
      expiring: list.filter((r) => r.status === "in_scadenza").length,
      expired: list.filter((r) => r.status === "scaduto").length,
      noDoc: list.filter(missingDoc).length,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toUpperCase();
    return (rows || []).filter((r) => {
      if (q && !r.name.includes(q) && !(r.supplier || "").toUpperCase().includes(q) && !(r.lot || "").includes(q)) return false;
      if (filter === "in_use") return r.lots_in_use > 0;
      if (filter === "alert") return r.status === "scaduto" || r.status === "in_scadenza";
      if (filter === "no_doc") return missingDoc(r);
      if (filter === "nessun_lotto") return r.status === "nessun_lotto";
      return true;
    });
  }, [rows, filter, query]);

  const chosen = useMemo(() => (rows || []).filter((r) => selected.includes(r.ingredient_id)), [rows, selected]);
  const site = sites.find((s) => s.id === siteId);
  const toggle = (id) => setSelected((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  const visibleIds = visible.map((r) => r.ingredient_id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleVisible = () => setSelected((current) => allVisibleSelected
    ? current.filter((id) => !visibleIds.includes(id))
    : [...new Set([...current, ...visibleIds])]);

  const print = () => {
    if (!chosen.length) return;
    if (format === "label62") { printQrLabels62x100(); return; }
    document.body.setAttribute("data-print", "raw-qr");
    setPrintPageSize(210, 297);
    setTimeout(() => { window.print(); document.body.removeAttribute("data-print"); clearPrintPageSize(); }, 60);
  };
  // PDF A4 vero: dal telefono è più affidabile della stampa diretta (lib/pdfExport.js).
  const createPdf = async () => {
    if (!chosen.length || !sheetRef.current) return;
    const win = preparePdfWindow();
    setPdfBusy(true);
    try {
      if (format === "label62") await qrLabels62x100Pdf(sheetRef.current, win, "materie-prime", site?.name);
      else await elementToPdf(sheetRef.current, { fileName: pdfFileName("qr-materie-prime", site?.name, zone), win, pageSelector: ".raw-qr-sheet" });
    } catch {
      toast.error("Impossibile creare il PDF. Riprova.");
    } finally {
      setPdfBusy(false);
    }
  };

  const sheetProps = { rows: chosen, format, company: ctx.tenant?.name, siteName: site?.name, zone };

  return (
    <div className="space-y-6" data-testid="raw-qr-page">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Documenti · Tracciabilità in entrata</p>
          <h1 className="font-heading text-3xl font-bold">QR materie prime</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">Un QR per ogni materia prima, da applicare su contenitore, scaffale o cella. <strong>Si stampa una sola volta</strong>: ogni volta che registri un nuovo lotto, chi inquadra il QR (anche l’ispettore ASL/NAS, senza login) trova subito <strong>lotto, fornitore, scadenza e DDT di appartenenza</strong>, i controlli fatti al ricevimento, le produzioni in cui il lotto è stato usato e lo storico dei lotti precedenti.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="success" onClick={createPdf} loading={pdfBusy} disabled={!chosen.length} data-testid="raw-qr-pdf">{!pdfBusy && <FileDown className="h-4 w-4" />} {pdfBusy ? "Creo il PDF…" : `Crea PDF e stampa${chosen.length ? ` (${chosen.length})` : ""}`}</Button>
          <Button variant="outline" onClick={print} disabled={!chosen.length} data-testid="raw-qr-print"><Printer className="h-4 w-4" /> Stampa diretta</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 no-print md:grid-cols-5" data-testid="raw-qr-stats">
        <Kpi label="Materie prime" value={stats.total} icon={Package} tone="text-navy" />
        <Kpi label="Con lotto in uso" value={stats.inUse} icon={CheckSquare} tone="text-emerald-700" />
        <Kpi label={`In scadenza ≤ ${expiringDays} gg`} value={stats.expiring} icon={AlertTriangle} tone="text-amber-700" onClick={() => setFilter("alert")} />
        <Kpi label="Scaduti in uso" value={stats.expired} icon={AlertTriangle} tone="text-red-700" onClick={() => setFilter("alert")} />
        <Kpi label="DDT da completare" value={stats.noDoc} icon={FileText} tone="text-sky-800" onClick={() => setFilter("no_doc")} />
      </div>

      <Card className="grid gap-4 p-5 no-print md:grid-cols-2">
        <Field label="Sede"><Select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="raw-qr-site">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
        <Field label="Zona di conservazione (facoltativo)" hint="Compare sui cartellini, es. DISPENSA, CELLA 1, FRIGO BAR."><Input value={zone} onChange={(e) => setZone(e.target.value.toUpperCase())} placeholder="ES. DISPENSA" data-testid="raw-qr-zone" /></Field>
        <div className="md:col-span-2">
          <Field label="Formato di stampa">
            <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
              {Object.entries(FORMATS).map(([key, f]) => (
                <button key={key} type="button" role="radio" aria-checked={format === key} onClick={() => setFormat(key)} title={f.hint}
                  className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs font-semibold transition-colors ${format === key ? "border-navy bg-navy text-white" : "border-hairline text-navy hover:bg-slate-50"}`} data-testid={`raw-qr-format-${key}`}>
                  <f.icon className="h-4 w-4 shrink-0" /> {f.label}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Card>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 no-print">Nessuna materia prima attiva: aggiungile da Materie prime, poi torna qui per stampare i QR.</Card>
      ) : <>
        <Card className="p-5 no-print">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca materia prima, fornitore o lotto" className="pl-9" data-testid="raw-qr-search" />
            </div>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={`min-h-[36px] rounded-full px-3 text-xs font-semibold ring-1 transition-colors ${filter === f.key ? "bg-navy text-white ring-navy" : "bg-white text-navy ring-hairline hover:bg-slate-50"}`} data-testid={`raw-qr-filter-${f.key}`}>{f.label}</button>
              ))}
            </div>
          </div>
          <button type="button" onClick={toggleVisible} disabled={!visible.length} className="mb-3 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-navy disabled:opacity-50" data-testid="raw-qr-toggle-all">
            {allVisibleSelected ? <CheckSquare className="h-5 w-5 text-emerald-600" /> : <Square className="h-5 w-5" />} {allVisibleSelected ? "Deseleziona quelle elencate" : "Seleziona quelle elencate"}
            <span className="font-normal text-slate-500">· {chosen.length} selezionate per la stampa</span>
          </button>
          {visible.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">Nessuna materia prima con questo filtro.</p> : (
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {visible.map((r) => <MaterialRow key={r.ingredient_id} row={r} checked={selected.includes(r.ingredient_id)} onToggle={() => toggle(r.ingredient_id)} />)}
            </div>
          )}
        </Card>

        {format === "label62" ? (
          chosen.length > 0 && <QrLabel62x100Sheet sheetRef={sheetRef} labels={chosen.map((r) => ({
            key: r.ingredient_id,
            url: qrUrl(r),
            kicker: "Materia prima · tracciabilità",
            title: r.name,
            note: r.allergens?.length > 0 ? `ALLERGENI: ${r.allergens.join(", ").toUpperCase()}` : null,
            scanText: "INQUADRA PER LOTTO, FORNITORE, SCADENZA E DDT AGGIORNATI",
            footer: `${ctx.tenant?.name?.toUpperCase() || ""}${zone ? ` — ${zone}` : site?.name ? ` — ${site.name.toUpperCase()}` : ""}`,
          }))} />
        ) : <>
          {chosen.length > 0 && (
            <div className="no-print overflow-x-auto" ref={sheetRef}>
              <RawQrSheets {...sheetProps} />
            </div>
          )}
          {/* Copia per la stampa portalata in document.body (come QrProduzioniCelle). */}
          {createPortal(<div className="print-area raw-qr-print"><RawQrSheets {...sheetProps} /></div>, document.body)}
        </>}
      </>}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`rounded-xl border border-hairline bg-white p-4 text-left shadow-card ${onClick ? "transition-all hover:-translate-y-0.5 hover:border-gold" : ""}`}>
      <div className={`flex items-start gap-2 text-xs font-semibold uppercase leading-tight tracking-wider ${tone}`}><Icon className="h-4 w-4 shrink-0" /> <span>{label}</span></div>
      <div className="mt-1 font-heading text-3xl font-bold text-navy">{value}</div>
    </Tag>
  );
}

function MaterialRow({ row: r, checked, onToggle }) {
  const st = STATUS[r.status] || STATUS.nessun_lotto;
  return (
    <label className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition-colors ${checked ? "border-emerald-300 bg-emerald-50/70" : "border-hairline hover:bg-slate-50"}`} data-testid={`raw-qr-row-${r.ingredient_id}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-0.5 h-5 w-5 accent-emerald-600" />
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2"><span className="font-medium text-navy">{r.name}</span><StatusBadge status={st.sem}>{st.label}</StatusBadge></span>
        {r.lots_in_use > 0 ? (
          <span className="mt-1 block text-xs leading-relaxed text-slate-600">
            Lotto <span className="font-mono font-semibold text-navy">{r.lot || "—"}</span> · Scad. {formatItalianDate(r.expiry_date)}
            {r.lots_in_use > 1 && <> · <strong>{r.lots_in_use} lotti in uso</strong></>}
            <span className="mt-0.5 flex flex-wrap items-center gap-x-1"><Truck className="h-3 w-3" /> {r.supplier || <span className="text-red-600">Fornitore mancante</span>}
              <span>·</span>
              {r.document?.number
                ? <span>{r.document.kind} n. {r.document.number}{r.document.pending && <span className="text-amber-700"> (documento da allegare)</span>}</span>
                : <span className="text-red-600">DDT non indicato</span>}
            </span>
          </span>
        ) : <span className="mt-1 block text-xs text-slate-500">Nessun lotto in uso: il QR è già valido e si aggiornerà al prossimo lotto registrato.</span>}
        <a href={qrUrl(r)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="mt-1 inline-flex min-h-[32px] items-center gap-1 text-xs font-medium text-sky-800 underline">Apri la scheda del QR <ExternalLink className="h-3 w-3" /></a>
      </span>
    </label>
  );
}

const PER_PAGE = { grid: 12, cards: 6 };

function RawQrSheets({ rows, format, company, siteName, zone }) {
  const size = PER_PAGE[format];
  const pages = [];
  for (let i = 0; i < rows.length; i += size) pages.push(rows.slice(i, i + size));
  return (
    <div className="space-y-6 print:space-y-0">
      {pages.map((page, index) => (
        <section key={index} className="raw-qr-sheet ingredient-a4-sheet bg-white text-navy" data-testid="raw-qr-sheet">
          {format === "grid" ? <>
            <header className="mb-5 border-b-2 border-navy pb-3 text-center">
              <div className="flex items-center justify-center gap-2"><Package className="h-6 w-6" strokeWidth={1.5} /><h2 className="font-heading text-3xl font-bold">QR MATERIE PRIME</h2></div>
              <p className="mt-1 text-lg font-bold">{zone || "TRACCIABILITÀ IN ENTRATA"}</p>
              <p className="mt-1 text-sm font-semibold">{company?.toUpperCase()}{siteName ? ` — ${siteName.toUpperCase()}` : ""}</p>
              <p className="mt-2 text-xs">INQUADRA IL QR CODE PER CONSULTARE LOTTO IN USO, FORNITORE, SCADENZA E DDT DI APPARTENENZA</p>
            </header>
            <div className="grid grid-cols-3 gap-4">
              {page.map((r) => (
                <div key={r.ingredient_id} className="break-inside-avoid rounded-lg border border-navy/30 p-3 text-center" style={{ breakInside: "avoid" }}>
                  <QRCodeCanvas value={qrUrl(r)} size={112} fgColor="#0B1D3A" level="M" className="mx-auto aspect-square h-auto max-w-full" />
                  <div className="mt-2 font-heading text-sm font-bold leading-tight">{r.name}</div>
                  <div className="mt-1 text-[9px] font-semibold tracking-wide">LOTTO · FORNITORE · SCADENZA · DDT</div>
                </div>
              ))}
            </div>
          </> : (
            <div className="grid grid-cols-2 gap-0">
              {page.map((r) => (
                <div key={r.ingredient_id} className="break-inside-avoid border border-dashed border-slate-300 p-3" style={{ breakInside: "avoid" }}>
                  <div className="flex h-full flex-col rounded-xl border-2 border-navy p-4 text-center">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-500">Materia prima · tracciabilità</div>
                    <div className="mt-1 font-heading text-xl font-bold leading-tight">{r.name}</div>
                    {r.allergens?.length > 0 && <div className="mt-1 text-[10px] font-bold text-red-700">ALLERGENI: {r.allergens.join(", ").toUpperCase()}</div>}
                    <QRCodeCanvas value={qrUrl(r)} size={150} fgColor="#0B1D3A" level="M" className="mx-auto mt-3 aspect-square h-auto max-w-full" />
                    <div className="mt-2 text-[9px] font-semibold leading-snug">INQUADRA PER LOTTO, FORNITORE,<br />SCADENZA E DDT AGGIORNATI</div>
                    <div className="mt-auto border-t border-navy/20 pt-2 text-[9px] font-medium">{company?.toUpperCase()}{zone ? ` — ${zone}` : siteName ? ` — ${siteName.toUpperCase()}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <footer className="mt-5 border-t border-navy/20 pt-2 text-center text-[9px]">QR fissi: si stampano una volta e mostrano sempre i lotti in uso, aggiornati a ogni nuovo carico. Dati di tracciabilità autorizzati, nessun dato personale.</footer>
        </section>
      ))}
    </div>
  );
}
